import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import showModal from "core-web/state/models/Modal/dispatches/showModal";
import formatDate from "core-web/utils/formatDate";
import hexWithOpacity from "core-web/utils/hexWithOpacity";
import Above from "core-web/components/Breakpoints/Above";
import Image from "core-web/components/Image";
import styled from "theme/libs/styled";
import ArrowIcon from "theme/components/icons/ArrowIcon";
import TextStyles from "theme/components/text/TextStyles";

const Wrapper = styled("div")`
    background: ${({ theme }) => theme.colors.white};
    padding: 24px;
    border-radius: 8px;
    display: grid;
    grid-template-columns: 110px 1fr auto;
    column-gap: 24px;
    align-items: center;
    cursor: pointer;

    ${({ theme }) => theme.below.tabletSm} {
        grid-template-columns: 85px 1fr auto;
        grid-template-rows: auto 35px;
    }
`;

const ImageWrapper = styled("div")`
    width: 110px;
    height: 110px;
    min-width: 110px;
    min-height: 110px;

    & img {
        border-radius: 100%;
    }

    ${({ theme }) => theme.below.tabletSm} {
        width: 85px;
        height: 85px;
        min-width: 85px;
        min-height: 85px;

        grid-area: 1 / 2 / 3 / 1;
    }
`;

const PromotionContent = styled("div")`
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    ${({ theme }) => theme.below.tabletSm} {
        display: grid;
        grid-template-columns: auto 40px;
        grid-template-rows: auto auto;
        grid-area: 1 / 2 / 3 / 4;
    }
`;

const OpenButton = styled("div")`
    border: 1px solid ${({ theme }) => theme.colors.black};
    border-radius: 999px;
    padding: 12px;
    width: 40px;
    height: 40px;

    display: flex;
    justify-content: center;
    align-items: center;

    align-self: flex-start;

    ${({ theme }) => theme.below.tabletSm} {
        align-self: flex-end;
        width: 28px;
        height: 28px;
        padding: 9px;

        grid-area: 2 / 3 / 2 / 3;

        & svg {
            width: 8px;
            height: 8px;
        }
    }
`;

const Expires = styled(TextStyles)`
    color: ${({ theme }) => hexWithOpacity(theme.colors.black, 60)};
    margin-top: 8px;

    ${({ theme }) => theme.below.tabletSm} {
        grid-area: 3 / 1 / 3 / 2;
    }
`;

const Description = styled(TextStyles)`
    color: ${({ theme }) => hexWithOpacity(theme.colors.black, 60)};

    ${({ theme }) => theme.below.tabletSm} {
        grid-area: 2 / 1 / 2 / 1;
    }
`;

const Title = styled(TextStyles)`
    margin-bottom: 8px;

    ${({ theme }) => theme.below.tabletSm} {
        grid-area: 1 / 1 / 2 / 3;
    }
`;

const Promotion = ({ data }) => {
    const { t } = useTranslation();
    const { name, description, imageUrl, expiresOn } = data ?? {};

    const handleOpen = () => {
        showModal("promotionModal", data);
    };

    return (
        <Wrapper onClick={handleOpen}>
            <ImageWrapper>{!!imageUrl && <Image src={imageUrl} objectFit="cover" />}</ImageWrapper>
            <PromotionContent>
                <Title textStyles={["Buenos Aires/16-120 SemiBold", null, null, "Buenos Aires/20-110 SemiBold"]}>
                    {name}
                </Title>
                <Description textStyles={["Avenir/12-140 Medium", null, null, "Avenir/16-140 Medium"]}>
                    <Above breakpoint="tabletSm" render={() => <>{description}</>} />
                </Description>
                <Expires textStyles="Buenos Aires/12-120 Regular">
                    {!!expiresOn && t("valid_until", { date: formatDate(new Date(expiresOn), "dd-MM-yy") })}
                </Expires>
            </PromotionContent>
            <OpenButton>
                <ArrowIcon direction="right" width="12px" height="12px" />
            </OpenButton>
        </Wrapper>
    );
};

Promotion.propTypes = {
    data: PropTypes.shape({
        name: PropTypes.string,
        description: PropTypes.oneOfType([PropTypes.string, PropTypes.exact(null)]),
        expiresOn: PropTypes.oneOfType([PropTypes.string, PropTypes.exact(null)]),
        imageUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.exact(null)]),
    }).isRequired,
    showMoreButton: PropTypes.bool,
    maxCount: PropTypes.number,
};

Promotion.defaultProps = {
    showMoreButton: false,
    maxCount: -1,
};

export default Promotion;
